import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, PageHeader, Button, Modal, Input, Select, Textarea, Spinner, SearchBar, EmptyState, StatusBadge } from "@/components/common";
import { Plus, Pencil, Trash2, HeartPulse, Bell } from "lucide-react";

const EMPTY = { person_id: "", tipo: "Agonistico", data_rilascio: "", scadenza: "", medico: "", note: "" };

export default function Certificati() {
  const { can } = useAuth();
  const editable = can("certificati", "edit");
  const [items, setItems] = useState(null);
  const [persone, setPersone] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/certificati").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/persone").then((r) => setPersone(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.person_id) { toast.error("Seleziona l'atleta"); return; }
    try { if (editId) await api.put(`/certificati/${editId}`, form); else await api.post("/certificati", form); toast.success("Salvato"); setOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/certificati/${id}`); load(); };
  const notify = async (id) => { try { await api.post(`/certificati/${id}/notify`); toast.success("Promemoria inviato via email"); } catch (e) { toast.error(apiErr(e)); } };

  if (!items) return <Spinner />;
  const stats = { green: 0, orange: 0, red: 0 }; items.forEach((c) => stats[c.status]++);
  const filtered = items.filter((c) => c.person_name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Certificati Medici" subtitle="Semaforo scadenze e promemoria automatici"
        actions={editable && <Button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="add-cert-btn"><Plus size={16} /> Nuovo certificato</Button>} />
      <div className="grid grid-cols-3 gap-4 mb-5">
        {[["red", "Scaduti", stats.red, "text-red-600"], ["orange", "In scadenza <30gg", stats.orange, "text-amber-600"], ["green", "Validi", stats.green, "text-emerald-600"]].map(([k, l, v, cls]) => (
          <Card key={k} className="p-4 flex items-center gap-3" data-testid={`cert-stat-${k}`}>
            <span className={`w-3 h-3 rounded-full ${k === "green" ? "bg-emerald-500" : k === "orange" ? "bg-amber-500" : "bg-red-500"}`} />
            <div><p className={`text-2xl font-bold font-display ${cls}`}>{v}</p><p className="text-xs text-slate-400">{l}</p></div>
          </Card>
        ))}
      </div>
      <div className="mb-4"><SearchBar value={q} onChange={setQ} placeholder="Cerca atleta..." /></div>
      {filtered.length === 0 ? <Card className="p-6"><EmptyState icon={HeartPulse} title="Nessun certificato" /></Card> : (
        <Card className="overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200 bg-slate-50/60 text-left">
            {["Atleta", "Tipo", "Rilascio", "Scadenza", "Medico", "Stato", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold text-slate-500 uppercase text-xs tracking-wider">{h}</th>)}
          </tr></thead>
          <tbody>{filtered.map((c) => (
            <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`cert-row-${c.id}`}>
              <td className="px-4 py-3 font-semibold text-slate-800">{c.person_name}</td>
              <td className="px-4 py-3 text-slate-600">{c.tipo}</td>
              <td className="px-4 py-3 text-slate-500">{c.data_rilascio || "—"}</td>
              <td className="px-4 py-3 text-slate-600 font-medium">{c.scadenza || "—"}</td>
              <td className="px-4 py-3 text-slate-500 text-xs">{c.medico || "—"}</td>
              <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
              <td className="px-4 py-3 text-right">{editable && <div className="flex justify-end gap-1">
                <button onClick={() => notify(c.id)} data-testid={`notify-cert-${c.id}`} title="Invia promemoria" className="p-1.5 rounded-lg hover:bg-sky-50 text-sky-600"><Bell size={15} /></button>
                <button onClick={() => { setForm({ ...EMPTY, ...c }); setEditId(c.id); setOpen(true); }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil size={15} /></button>
                <button onClick={() => del(c.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button></div>}</td>
            </tr>
          ))}</tbody>
        </table></div></Card>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifica certificato" : "Nuovo certificato"}>
        <div className="space-y-4">
          <Select label="Atleta" data-testid="cert-person" value={form.person_id} onChange={(e) => upd("person_id", e.target.value)}>
            <option value="">Seleziona...</option>{persone.map((p) => <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>)}
          </Select>
          <Select label="Tipo" value={form.tipo} onChange={(e) => upd("tipo", e.target.value)}><option>Agonistico</option><option>Non agonistico</option><option>Ludico-motorio</option></Select>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Data rilascio" type="date" value={form.data_rilascio} onChange={(e) => upd("data_rilascio", e.target.value)} />
            <Input label="Scadenza" type="date" data-testid="cert-scadenza" value={form.scadenza} onChange={(e) => upd("scadenza", e.target.value)} />
          </div>
          <Input label="Medico / Centro" value={form.medico} onChange={(e) => upd("medico", e.target.value)} />
          <Textarea label="Note" rows={2} value={form.note} onChange={(e) => upd("note", e.target.value)} />
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} data-testid="save-cert-btn">Salva</Button></div>
        </div>
      </Modal>
    </div>
  );
}
